import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { useAnchoredPosition, useEscapeKey } from '@/hooks'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import type { Assignee } from '@/types'
import { AssigneePicker } from './AssigneePicker'
import { useCreateActionItem } from '../hooks'
import type { AssigneePayload } from '../api'

/**
 * Add a commitment the summariser never had a chance to hear.
 *
 * Two of these are ordinary and both were previously impossible: a task agreed
 * in the corridor after the call, and one the model simply missed. The list
 * people work from was only ever as complete as one model's reading of one
 * recording.
 *
 * It needs a MEETING, which is why this lives on a meeting's detail page rather
 * than on the workspace-wide list — an action item is evidence about a
 * conversation, and one belonging to no meeting would have nowhere to sit, no
 * roster to assign from, and nobody to show it to.
 */
export function AddActionItem({ meetingId }: { meetingId: string }) {
  const t = useT()
  const [isOpen, setIsOpen] = useState(false)
  const [text, setText] = useState('')
  const [dueDay, setDueDay] = useState('')
  const [assignee, setAssignee] = useState<AssigneePayload | null>(null)
  const [preview, setPreview] = useState<Assignee | null>(null)
  const create = useCreateActionItem()
  const triggerRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)

  const reset = useCallback(() => {
    setText('')
    setDueDay('')
    setAssignee(null)
    setPreview(null)
    setIsOpen(false)
  }, [])

  useEscapeKey(reset, isOpen)

  // The form is portaled to `document.body` (below), so it is no longer a DOM
  // descendant of the trigger -- a click inside it would otherwise look like
  // an outside click and close the form on itself. Checking both refs is also
  // what keeps a click on the trigger from double-toggling: the trigger is
  // inside `triggerRef`, so this handler leaves it alone and the trigger's own
  // `onClick` is the only thing that flips `isOpen`.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (formRef.current?.contains(target)) return
      // The assignee picker's own dropdown is ALSO portaled to `document.body`
      // (its own `overflow-hidden`-ancestor fix), so it is a sibling of
      // `formRef` rather than a descendant even though it belongs to a control
      // rendered inside this form. Its panel already carries `role="listbox"`;
      // treat a click there as inside, or picking a candidate would close and
      // reset this form out from under the click.
      if (target instanceof Element && target.closest('[role="listbox"]')) return
      reset()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [isOpen, reset])

  // The trigger sits at the bottom of a list that can run long, so the form
  // must flip upward instead of opening into (or past) the fold. Portaled so
  // an `overflow-hidden` ancestor (Card, a scroll region) cannot clip it.
  const position = useAnchoredPosition(triggerRef, formRef, {
    isOpen,
    side: 'bottom',
    align: 'start',
  })

  const trimmed = text.trim()

  const submit = () => {
    if (!trimmed) return
    create.mutate(
      {
        meetingId,
        text: trimmed,
        dueDate: dueDay ? `${dueDay}T00:00:00Z` : null,
        ...(assignee ? { assignee } : {}),
      },
      { onSuccess: reset },
    )
  }

  return (
    <div ref={triggerRef} className="inline-block">
      <Button
        variant="secondary"
        leftIcon={<Plus className="h-4 w-4" />}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {t('addAction.add')}
      </Button>
      {isOpen &&
        createPortal(
          <div
            ref={formRef}
            style={{ position: 'fixed', top: position?.top ?? 0, left: position?.left ?? 0 }}
            className={cn(
              'z-popover w-[26rem] max-w-[calc(100vw-2rem)]',
              // Hidden until measured against the trigger and the viewport, so
              // it never flashes at (0, 0) before `useAnchoredPosition` places it.
              !position && 'invisible',
            )}
          >
            <Card className="p-3 shadow-lg">
              <textarea
                autoFocus
                rows={2}
                value={text}
                maxLength={2000}
                placeholder={t('addAction.placeholder')}
                aria-label={t('addAction.text')}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    submit()
                  }
                  if (event.key === 'Escape') reset()
                }}
                className="w-full rounded-md border border-border bg-surface-sunken px-2.5 py-2 text-base text-fg placeholder:text-fg-subtle focus:outline focus:outline-2 focus:outline-brand"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <AssigneePicker
                  meetingId={meetingId}
                  current={preview}
                  onSelect={(next) => {
                    setAssignee(next)
                    // Shown optimistically from what was picked. The server decides
                    // which of the three assignee shapes it is, and the created item
                    // comes back carrying the real answer.
                    setPreview(
                      next
                        ? {
                            kind: next.email ? 'external' : 'member',
                            id: next.id ?? null,
                            displayName: next.name || next.email || t('addAction.selectedPerson'),
                            email: next.email ?? null,
                            avatarUrl: null,
                            attendedMeeting: false,
                          }
                        : null,
                    )
                  }}
                />
                <label className="inline-flex items-center gap-2 text-sm text-fg-muted">
                  <span>{t('addAction.due')}</span>
                  <input
                    type="date"
                    value={dueDay}
                    onChange={(event) => setDueDay(event.target.value)}
                    className="rounded-md border border-border bg-surface-sunken px-1.5 py-0.5 text-sm text-fg focus:outline focus:outline-2 focus:outline-brand"
                  />
                </label>
                <span className="flex-1" />
                <Button size="sm" onClick={submit} disabled={!trimmed || create.isPending}>
                  {create.isPending ? t('common.adding') : t('common.add')}
                </Button>
                <Button size="sm" variant="ghost" onClick={reset}>
                  {t('common.cancel')}
                </Button>
              </div>
            </Card>
          </div>,
          document.body,
        )}
    </div>
  )
}
